/**
 * Five-Year Forecast helpers shared by the list and entry screens.
 *
 * Months are addressed by month_index 1..60 relative to the forecast's start
 * month/year (1-12 = Year 1, 13-24 = Year 2, ...), matching gl-api.
 * Percent inputs are typed as whole percents (10 = 10%) and stored as
 * fractions (0.10) in a line's params.
 */

export const FORECAST_MONTHS = 60;
export const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const MONTH_FULL_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August',
    'September', 'October', 'November', 'December'];

export type ForecastMethod =
    'units_price' | 'subscription' | 'fixed_monthly' | 'percent_income' | 'annual_growth' | 'direct_monthly'
    | 'linked_units' | 'headcount_salary' | 'percent_salaries' | 'per_employee' | 'tax_rate';

/** Income methods that produce monthly units a COGS line can link to. */
export const UNIT_METHODS: ForecastMethod[] = ['units_price', 'subscription'];

// 'income_line'   = pick a units-based income line of the same forecast (stored as its id).
// 'base_line'     = pick any non-% income line as the base of a '% of' line (stored as income_line_id).
// 'percent_years' = one % per forecast year (Year 1-5), stored as <key>_by_year; a blank
//                   year carries the previous year's rate, so entering Year 1 covers all five.
// 'currency_years' = one $ amount per forecast year, stored as <key>_by_year (blank year carries forward).
// 'number_years'   = one plain number per forecast year (units), stored as <key>_by_year.
export type FieldKind = 'number' | 'currency' | 'percent' | 'select' | 'income_line' | 'base_line' | 'percent_years'
    | 'currency_years' | 'number_years';

/** Fields entered once per forecast year (Year 1-5). */
export const isYearsField = (kind: FieldKind) =>
    kind === 'percent_years' || kind === 'currency_years' || kind === 'number_years';

export const FORECAST_YEARS = 5;

/** Max length of a line's Product / Description. */
export const DESCRIPTION_MAX = 100;

/**
 * Blocks keys that type="number" inputs would otherwise accept as raw
 * characters: 'e'/'E' (scientific notation) and '+'/'-' (signs - all
 * assumption inputs are nonnegative anyway). Digits, '.' and control keys
 * still go through.
 */
export const numKeyDown = (e: { key: string; preventDefault(): void }) => {
    if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
};

/** Numeric inputs accept at most 2 decimal places; blanks pass through. */
export const fitsDecimals = (v: any): boolean =>
    v === '' || v === undefined || v === null || !/\.\d{3}/.test(String(v));

/** Typed values cap at 16 digits (same bound as assumption fields / starting cash). */
export const MAX_ENTRY = 1e16;

/** Numeric entries: 2 decimals max and within the 16-digit bound; blanks pass. */
export const fitsAmount = (v: any): boolean =>
    fitsDecimals(v) &&
    (v === '' || v === undefined || v === null ||
    (Number.isFinite(Number(v)) && Math.abs(Number(v)) < MAX_ENTRY));

export interface MethodField {
    key: string;
    label: string;
    kind: FieldKind;
    options?: { value: string; label: string }[];
    defaultValue?: any;
    i18nKey?: string;   // defaults to field.<key>
    required?: boolean; // shown with a * and must be filled before the line can be saved
    tip?: string;       // short guidance shown by the info icon next to the label
    when?: { key: string; value: string };   // only shown / saved when another field has this value
    noCarry?: boolean;  // years field where a blank year means "continue", not "repeat the previous value"
    cats?: string[];    // only offered on these entry tabs (e.g. a per-expense headcount input)
}

export const METHOD_LABELS: Record<ForecastMethod, string> = {
    units_price: 'Units × Price',
    subscription: 'Subscription',
    fixed_monthly: 'Fixed Monthly',
    percent_income: '% Income',
    annual_growth: 'Annual Growth',
    direct_monthly: 'Direct Monthly Entry',
    linked_units: 'Units from Income Product',
    headcount_salary: 'Headcount × Salary',
    percent_salaries: '% of Salaries',
    per_employee: 'Per Employee',
    tax_rate: 'Tax Rate %',
};

/** Inputs shown for each method. Keys are the params stored on the line. */
export const METHOD_FIELDS: Record<ForecastMethod, MethodField[]> = {
    units_price: [
        { key: 'units', label: 'Units sold per month',
          kind: 'number_years', noCarry: true, i18nKey: 'field.units_by_year', required: true,
          tip: 'Enter the expected number of units sold each month.' },
        { key: 'units_step', label: 'Units added each month', kind: 'number_years', i18nKey: 'field.units_step_by_year' },
        { key: 'growth_pct', label: 'Monthly growth %', kind: 'percent_years' },
        { key: 'price', label: 'Price per unit', kind: 'currency', required: true,
          tip: 'Enter the selling price per unit (up to 16 digits and 2 decimal places).' },
        { key: 'annual_price_increase_pct', label: 'Annual price increase %', kind: 'percent' },
    ],
    subscription: [
        { key: 'existing_subscribers', label: 'Existing subscribers at start', kind: 'number' },
        { key: 'new_subscribers', label: 'New subscribers per month', kind: 'number', required: true,
          tip: 'Enter the expected number of new subscribers each month.' },
        { key: 'growth_pct', label: 'Monthly growth % of new subscribers', kind: 'percent_years', i18nKey: 'field.growth_pct_new' },
        { key: 'churn_pct', label: 'Monthly churn %', kind: 'percent_years' },
        { key: 'price', label: 'Monthly price per subscriber', kind: 'currency', required: true,
          tip: 'Enter the monthly subscription price per subscriber.' },
        { key: 'annual_price_increase_pct', label: 'Annual price increase %', kind: 'percent' },
    ],
    fixed_monthly: [
        { key: 'amount', label: 'Monthly amount', kind: 'currency_years', i18nKey: 'field.amount_by_year', required: true,
          tip: 'Enter the expected amount for each month.' },
        { key: 'annual_increase_pct', label: 'Annual increase %', kind: 'percent' },
    ],
    percent_income: [
        { key: 'percent', label: 'Percentage', kind: 'percent', required: true,
          tip: 'Enter the percentage to apply to the selected base.' },
        {
            key: 'base', label: 'Percentage of', kind: 'select', defaultValue: 'gross_revenue',
            options: [
                { value: 'gross_revenue', label: 'Gross revenue' },
                { value: 'net_revenue', label: 'Revenue after adjustments' },
                { value: 'income_line', label: 'A specific income line' },
            ],
        },
        { key: 'income_line_id', label: 'Income line', kind: 'base_line', i18nKey: 'field.base_line',
          when: { key: 'base', value: 'income_line' }, required: true,
          tip: 'Select the income line used as the base for the percentage calculation.' },
    ],
    annual_growth: [
        { key: 'year1_amount', label: 'Year 1 amount', kind: 'currency', required: true,
          tip: 'Enter the total amount expected for the first forecast year.' },
        { key: 'annual_growth_pct', label: 'Annual growth %', kind: 'percent', required: true,
          tip: 'Enter the annual growth percentage to apply from the second forecast year onward.' },
    ],
    direct_monthly: [],
    // COGS: units come from the linked income line every month; only the cost is entered.
    linked_units: [
        { key: 'income_line_id', label: 'Income product', kind: 'income_line', required: true,
          tip: 'Select the income product whose units sold will be used to calculate this cost.' },
        { key: 'unit_cost', label: 'Cost per unit', kind: 'currency', required: true,
          tip: 'Enter the cost for each unit sold.' },
        { key: 'annual_cost_increase_pct', label: 'Annual cost increase %', kind: 'percent' },
    ],
    // Payroll: salary lines drive total salaries and headcount, which the other two use.
    headcount_salary: [
        { key: 'headcount', label: 'Headcount', kind: 'number', required: true,
          tip: 'Enter the number of employees included in this forecast.' },
        { key: 'salary', label: 'Monthly salary per person', kind: 'currency', required: true,
          tip: 'Enter the monthly salary for each employee.' },
        { key: 'raise_pct', label: 'Annual raise %', kind: 'percent' },
    ],
    percent_salaries: [
        { key: 'percent', label: '% of total salaries', kind: 'percent', i18nKey: 'field.percent_salaries', required: true,
          tip: 'Enter the percentage of total salaries allocated to this expense.' },
    ],
    per_employee: [
        { key: 'amount', label: 'Monthly amount per employee', kind: 'currency', i18nKey: 'field.amount_per_employee', required: true,
          tip: 'Enter the expected monthly cost per employee.' },
        // On expense lines the count can be typed; on payroll lines it comes from Headcount x Salary lines.
        { key: 'headcount', label: 'Employee count', kind: 'number', i18nKey: 'field.employee_count', cats: ['operating_expense'], required: true,
          tip: 'Enter the number of employees this cost applies to.' },
        { key: 'annual_increase_pct', label: 'Annual increase %', kind: 'percent' },
    ],
    tax_rate: [
        { key: 'rate', label: 'Tax rate % of income before taxes', kind: 'percent', required: true,
          tip: 'Enter the tax rate applied to income before taxes.' },
    ],
};

/** Income lines a COGS line can link to, with a readable label. */
export const linkableIncomeLines = (lines: any[] = []) =>
    lines
        .filter((l) => l.category === 'income' && UNIT_METHODS.includes(l.method))
        .map((l) => ({
            value: l.id,
            label: `${l.account_name || ''}${l.description ? ` / ${l.description}` : ''} (${METHOD_LABELS[l.method as ForecastMethod]})`,
        }));

/** Income lines a '% of' line can use as its base (any income line that is not itself a %). */
export const baseIncomeLines = (lines: any[] = [], excludeId?: number) =>
    lines
        .filter((l) => l.category === 'income' && l.method !== 'percent_income' && l.id !== excludeId)
        .map((l) => ({
            value: l.id,
            label: `${l.account_name || ''}${l.description ? ` / ${l.description}` : ''}`,
        }));

/** Whether a method field applies given the other field values (see MethodField.when). */
export const fieldApplies = (f: MethodField, values: Record<string, any>): boolean =>
    !f.when || (values[f.when.key] ?? '') === f.when.value;

/** Label for a month_index given the forecast start, e.g. (1, 9, 2026) -> "Sep 2026". */
export const monthLabel = (monthIndex: number, startMonth: number, startYear: number): string => {
    const zero = (startMonth - 1) + (monthIndex - 1);
    return `${MONTH_NAMES[zero % 12]} ${startYear + Math.floor(zero / 12)}`;
};

/** Options for a "start month" select over the 60 forecast months. */
export const monthOptions = (startMonth: number, startYear: number) =>
    Array.from({ length: FORECAST_MONTHS }, (_, i) => ({
        value: i + 1,
        label: `${monthLabel(i + 1, startMonth, startYear)} (Year ${Math.floor(i / 12) + 1})`,
    }));

/** The 12 month_index values of a forecast year (1-based). */
export const yearMonthIndexes = (year: number): number[] =>
    Array.from({ length: 12 }, (_, i) => (year - 1) * 12 + i + 1);

const toFraction = (n: number) => Math.round(n * 10000) / 1000000;
const toWhole = (v: any) => Math.round(Number(v) * 1000000) / 10000;

/** Form values (percents as whole numbers, strings) -> params stored on the line. */
export const toParams = (method: ForecastMethod, values: Record<string, any>): Record<string, any> => {
    const params: Record<string, any> = {};
    const withDefaults: Record<string, any> = { ...values };
    METHOD_FIELDS[method].forEach((f) => {
        if (f.kind === 'select' && !withDefaults[f.key]) withDefaults[f.key] = f.defaultValue;
    });
    METHOD_FIELDS[method].forEach((f) => {
        if (!fieldApplies(f, withDefaults)) return;
        const raw = values[f.key];
        if (isYearsField(f.kind)) {
            const conv = f.kind === 'percent_years' ? toFraction : (n: number) => n;
            const years: (number | null)[] = Array.from({ length: FORECAST_YEARS }, (_, i) => {
                const v = Array.isArray(raw) ? raw[i] : (i === 0 ? raw : '');
                return v === '' || v === null || v === undefined || Number.isNaN(Number(v)) ? null : conv(Number(v));
            });
            while (years.length && years[years.length - 1] === null) years.pop();
            if (years.length) {
                params[`${f.key}_by_year`] = years;
                params[f.key] = years[0] ?? 0;   // Year 1 value, also kept as the single value
            }
            return;
        }
        if (f.kind === 'select') {
            params[f.key] = raw || f.defaultValue;
            return;
        }
        if (raw === '' || raw === null || raw === undefined) return;
        const n = Number(raw);
        if (Number.isNaN(n)) return;
        params[f.key] = f.kind === 'percent' ? toFraction(n) : n;
    });
    return params;
};

/** Params stored on the line -> form values (percents back to whole numbers). */
export const fromParams = (method: ForecastMethod, params: Record<string, any> = {}): Record<string, any> => {
    const values: Record<string, any> = {};
    METHOD_FIELDS[method].forEach((f) => {
        const v = params[f.key];
        if (isYearsField(f.kind)) {
            const byYear = params[`${f.key}_by_year`];
            const conv = f.kind === 'percent_years' ? toWhole : (n: any) => n;
            values[f.key] = Array.from({ length: FORECAST_YEARS }, (_, i) => {
                const y = Array.isArray(byYear) ? byYear[i] : (i === 0 ? v : null);
                return y === null || y === undefined ? '' : conv(y);
            });
            return;
        }
        if (v === undefined || v === null) {
            values[f.key] = f.kind === 'select' ? f.defaultValue : '';
        } else {
            values[f.key] = f.kind === 'percent' ? toWhole(v) : v;
        }
    });
    return values;
};

/** Short human summary of a line's assumptions for the "included" table. */
export const summarizeParams = (method: ForecastMethod, params: Record<string, any> = {}, lines: any[] = []): string => {
    const money = (v: any) => `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
    const pct = (v: any) => `${Math.round(Number(v) * 1000000) / 10000}%`;
    return (METHOD_FIELDS[method] || [])
        .filter((f) => params[f.key] !== undefined && params[f.key] !== null && params[f.key] !== '')
        .filter((f) => fieldApplies(f, params))
        .map((f) => {
            const v = params[f.key];
            if (f.kind === 'select') {
                if (v === 'income_line') return '';
                return f.options?.find((o) => o.value === v)?.label || v;
            }
            if (f.kind === 'base_line') {
                const base = lines.find((l) => l.id === Number(v));
                return `Of: ${base ? `${base.account_name}${base.description ? ` / ${base.description}` : ''}` : `line #${v}`}`;
            }
            if (isYearsField(f.kind)) {
                const fmt = f.kind === 'percent_years' ? pct
                    : f.kind === 'number_years' ? (n: any) => Number(n).toLocaleString() : money;
                if (f.noCarry) {
                    const byYear: any[] = Array.isArray(params[`${f.key}_by_year`]) ? params[`${f.key}_by_year`] : [v];
                    const typed = byYear.map((x, i) => (x === null || x === undefined ? '' : `Y${i + 1} ${fmt(x)}`)).filter((x) => x);
                    return `Units: ${typed.join(', ')}`;
                }
                const byYear: any[] = Array.isArray(params[`${f.key}_by_year`]) ? params[`${f.key}_by_year`] : [v];
                let last: any = 0;
                const filled = Array.from({ length: FORECAST_YEARS }, (_, i) => {
                    if (byYear[i] !== null && byYear[i] !== undefined) last = byYear[i];
                    return fmt(last);
                });
                const same = filled.every((x) => x === filled[0]);
                const name = f.kind === 'percent_years' ? f.label.replace(/ %.*$/, '') : f.label.replace(/,? by year$/, '');
                return `${name}: ${same ? filled[0] : filled.join(' / ')}`;
            }
            if (f.kind === 'income_line') {
                const linked = lines.find((l) => l.id === Number(v));
                return `Units from: ${linked ? `${linked.account_name}${linked.description ? ` / ${linked.description}` : ''}` : `line #${v}`}`;
            }
            if (f.kind === 'percent') return `${f.label.replace(' %', '')}: ${pct(v)}`;
            if (f.kind === 'currency') return `${f.label}: ${money(v)}`;
            return `${f.label}: ${Number(v).toLocaleString()}`;
        })
        .concat(Object.keys(params.units_by_month || {}).length
            ? [`Units typed for ${Object.keys(params.units_by_month).length} month(s)`] : [])
        .filter((x) => x)
        .join(' · ');
};

/** Cash & Financing item keys saved by the first release -> detailed items (mirrors gl-api). */
export const LEGACY_CASH_ITEMS: Record<string, string> = {
    debt_proceeds: 'loan_long_term',
    equity_contributions: 'paid_in_capital',
    capital_expenditures: 'capital_expenditure',
    debt_principal: 'principal_long_term',
    distributions: 'dividends',
};

export const cashItemKey = (key?: string): string => (key && LEGACY_CASH_ITEMS[key]) || key || '';

/** Cash flow row labels: income-statement rows and Cash & Financing items (mirrors gl-api cash_items.py). */
export const CASH_ROW_LABELS: Record<string, string> = {
    operating_receipts: 'Revenue from Operations',
    cogs: 'Cost of Goods Sold',
    payroll: 'Payroll',
    operating_expenses: 'Operating Expenses',
    interest: 'Interest Payments',
    taxes: 'Taxes',
    loan_short_term: 'Loan (Short Term)',
    loan_long_term: 'Loan / Note (Long Term)',
    line_of_credit: 'Line of Credit',
    paid_in_capital: 'Paid-In Capital',
    equity_partner: 'Equity Partner',
    safe: 'SAFE',
    other_cash_in: 'Other Cash In',
    principal_short_term: 'Principal Payment - Loan (Short Term)',
    principal_long_term: 'Principal Payment - Loan / Note (Long Term)',
    principal_line_of_credit: 'Principal Payment - Line of Credit',
    capital_expenditure: 'Capital Expenditure',
    lease_payment: 'Lease Payment',
    other_payment: 'Other Payment',
    dividends: 'Dividend Payment',
    asset_sale_proceeds: 'Sale of Assets (Proceeds)',
    intangible_purchase: 'Intangible Asset Purchase',
};

/** "Jan 2027" or "Jan 2027 – Dec 2028" for a line's start / optional end month. */
export const lineSpan = (line: any, startMonth: number, startYear: number): string => {
    const start = monthLabel(line.start_month_index || 1, startMonth, startYear);
    return line.end_month_index ? `${start} – ${monthLabel(line.end_month_index, startMonth, startYear)}` : start;
};
